import { BufferGeometry, Euler, Mesh, Quaternion, Vector3 } from 'three';
import { DEG2RAD } from '@constants/MathGeometry';
import RaycastEvents from '@modules/three/common/raycastEvents.ts';
import JawCorrection from './JawCorrection.ts';
import TransformMesh from '@modules/three/common/transformMesh/TransformMesh.ts';
import { createToothMaterial } from '@modules/three/common/materials/jawMaterials.ts';
import { Vertex, Face, HalfedgeDS } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { ToothSegmentationResponse } from '@/schema/toothSegmentation';
import { ToothCorrectionCreate } from '@/schema/toothCorrection';

export type gumVertex = Vertex;
export type toothVertex = Vertex;

export default class ToothCorrection extends TransformMesh implements RaycastEvents {
  private centralAxis = new Vector3();
  private jaw: JawCorrection;
  /**
   * Orthodontic reference frame of this tooth, fixed at the INITIAL pose:
   * X = mesiodistal (along the arch), Y = long axis (occlusal), Z = X×Y
   * (buccolingual). Maps frame-local vectors to world. The gizmo pivot, the
   * UI readout and the treatment-cycle limits all run in this frame; the
   * persisted DTO stays world-space (getMovedPosition/getMovedRotQuaternion).
   */
  private orthoFrame = new Quaternion();
  private readonly FDI: number; // TODO FDI notation change later and use teethIDs
  private verticesAroundGum: Map<toothVertex, gumVertex> = new Map();
  /**
   * Index of the first face segmentation GENERATED for this crown — the
   * interproximal wall it drew for the SCAN positions. The print export
   * deletes that suffix and rebuilds the space between the posed crowns
   * (see interproximalRebuild). Null when segmentation generated nothing, or
   * when the model predates the marker.
   */
  public readonly generatedFacesFrom: number | null;
  /**
   * The index buffer exactly as the PLY delivered it — the ONLY face order
   * `generatedFacesFrom` is valid in. Captured before super(), because
   * setNewGeometry runs computeBoundsTree and three-mesh-bvh reorders the
   * live geometry's index in place; by the time the export bakes this tooth,
   * the live order is BVH leaf order and the marker means nothing there.
   */
  public readonly plyFaceOrder: Uint32Array | null;

  constructor(model: BufferGeometry, data: ToothSegmentationResponse, jaw: JawCorrection) {
    const plyIndex = model.getIndex();
    const plyFaceOrder = plyIndex ? Uint32Array.from(plyIndex.array) : null;
    super(model);
    this.plyFaceOrder = plyFaceOrder;
    this.jaw = jaw;
    this.FDI = jaw.mapToFDI(data.fdi);
    this.generatedFacesFrom = data.generatedFacesFrom ?? null;
    this.centralAxis.fromArray(data.cameraPosition);

    this.centerOrigin();
    this.setPivotTransform(this.position.clone(), this.centralAxis);
    // centerOrigin() updates the buffer and syncs half-edge positions via syncHalfEdgePositionsFromGeometry()

    // After centerOrigin() modified vertex positions, rebuild BVH for closest-point queries
    this.geometry.computeBoundingBox();
    this.geometry.computeBoundsTree();

    this.material = createToothMaterial();
    //this.fillMesh();

    this.rootState.scene.add(this.pivot);
    this.rootState.scene.add(this as Mesh);
    // Ensure matrices are up to date before any world/local conversions
    this.rootState.scene.updateMatrixWorld(true);
  }

  public raycastHit() {
    this.detach(); // clear old listeners & target
    this.jaw.getJawsCorrection().setActiveTooth(this); // update one tooth top-left UI
    this.attachGizmo(this.moveTooth.bind(this));
  }

  private moveTooth() {
    // Move gum vertices attached to this tooth rigidly (no re-snapping)
    this.jaw.updateGumVerticesAttachedToTooth(this);
    const rotationDelta = this.getMovedRotQuaternion();
    this.jaw
      .getTransformIterations()
      .calculateIterations(this.FDI, this.getMovedPosition(), rotationDelta, this.orthoFrame);
    this.jaw.getJawsCorrection().setActiveTooth(this); // force Zustand → React keep updating
  }

  public getFDI(): number {
    return this.FDI;
  }

  public getMovedPosition(): Vector3 {
    return this.position.clone().sub(this.initialPosition);
  }

  public getMovedRotQuaternion(): Quaternion {
    const currentOrientation = this.quaternion.clone();
    const initialInverse = this.initialMeshQuat.clone().invert();
    return currentOrientation.multiply(initialInverse);
  }

  public getVerticesAroundGum(): Map<Vertex, Vertex> {
    return this.verticesAroundGum;
  }

  public setVerticesAroundGum(verticesAroundGum: Map<Vertex, Vertex>) {
    this.verticesAroundGum = verticesAroundGum;
  }

  public getCentralAxis(): Vector3 {
    return this.centralAxis;
  }

  public getOrthoFrame(): Quaternion {
    return this.orthoFrame.clone();
  }

  /**
   * Installs the orthodontic frame and re-orients the gizmo pivot to it, so
   * the local-space handles line up with the anatomy. Init-only, like
   * setPivotOrientation — must run before any user interaction.
   */
  public setOrthoFrame(frame: Quaternion) {
    this.orthoFrame.copy(frame);
    this.setPivotOrientation(frame);
  }

  /** Movement delta expressed in the orthodontic frame (mm per axis). */
  public getMovedPositionOrtho(): Vector3 {
    return this.getMovedPosition().applyQuaternion(this.orthoFrame.clone().invert());
  }

  /** Rotation delta conjugated into the orthodontic frame. */
  public getMovedRotQuaternionOrtho(): Quaternion {
    return this.orthoFrame
      .clone()
      .invert()
      .multiply(this.getMovedRotQuaternion())
      .multiply(this.orthoFrame);
  }

  /**
   * Applies an ABSOLUTE offset typed in the orthodontic frame (mm / degrees
   * per axis, same values the readout shows) and runs the full release
   * pipeline, exactly as if the user had dragged the gizmo and let go.
   * Values are clamped to the same limits the gizmo enforces
   * (translateLimit / ±rotateLimitDeg per world Euler axis) — without the
   * clamp the first objectChange of the next drag would re-clamp and jump.
   */
  public applyOrthoTransform(posMm: Vector3, rotDeg: Vector3) {
    const offset = posMm.clone().applyQuaternion(this.orthoFrame);
    if (offset.length() > this.translateLimit) {
      offset.setLength(this.translateLimit);
    }
    const targetPosition = this.getInitialPosition().clone().add(offset);

    const frameDelta = new Quaternion().setFromEuler(
      new Euler(rotDeg.x * DEG2RAD, rotDeg.y * DEG2RAD, rotDeg.z * DEG2RAD, 'XYZ'),
    );
    const worldDelta = this.orthoFrame
      .clone()
      .multiply(frameDelta)
      .multiply(this.orthoFrame.clone().invert());
    const rawQuat = worldDelta.multiply(this.getInitialQuaternion()).normalize();
    const euler = new Euler().setFromQuaternion(rawQuat, 'XYZ');
    const max = this.rotateLimitDeg * DEG2RAD;
    euler.x = Math.max(-max, Math.min(max, euler.x));
    euler.y = Math.max(-max, Math.min(max, euler.y));
    euler.z = Math.max(-max, Math.min(max, euler.z));
    const targetQuat = new Quaternion().setFromEuler(euler);

    this.setPosition(targetPosition);
    this.setRotationFromQuaternion(targetQuat);
    this.rotateHalfEdges(targetQuat, targetPosition);
    this.syncPivotToMeshTransform();

    this.moveTooth();
    this.geometry.computeBoundingBox();
    this.geometry.computeBoundsTree();
    this.geometry.boundsTree?.refit();
    this.jaw.onActiveToothTransformEnd(this);
  }

  /**
   * Converts the current tooth correction data into a DTO with movement and rotation.
   */
  public toCorrectionDTO(): ToothCorrectionCreate {
    const movement = this.getMovedPosition();
    const rotation = this.getMovedRotQuaternion();
    return {
      fdi: this.getFDI(),
      movement: [movement.x, movement.y, movement.z],
      rotation: [rotation.x, rotation.y, rotation.z, rotation.w],
    };
  }

  /**
   * Re-applies a movement/rotation delta read back from a stored correction —
   * the inverse of toCorrectionDTO. Unlike moveTooth this reports nothing to the
   * jaw: no gum diffusion, no treatment-cycle bookkeeping, no store update. It
   * exists for rebuilding a saved case off-screen (see buildPrintableJawModels),
   * where the gum arrives already deformed and the UI is not on the other end.
   */
  public applySavedCorrection(movement: number[], rotation: number[]) {
    const [mx = 0, my = 0, mz = 0] = movement ?? [0, 0, 0];
    const [qx = 0, qy = 0, qz = 0, qw = 1] = rotation ?? [0, 0, 0, 1];

    const targetPosition = this.getInitialPosition().clone().add(new Vector3(mx, my, mz));
    this.setPosition(targetPosition);

    const targetRotation = this.getInitialQuaternion()
      .clone()
      .multiply(new Quaternion(qx, qy, qz, qw).normalize())
      .normalize();
    this.setRotationFromQuaternion(targetRotation);
    this.rotateHalfEdges(targetRotation, targetPosition);
    this.geometry.attributes.position.needsUpdate = true;
  }

  public resetPosition() {
    super.resetPosition();

    this.moveTooth();
    this.geometry.computeBoundingBox();
    this.geometry.computeBoundsTree();
    this.geometry.boundsTree?.refit();
    this.jaw.onActiveToothTransformEnd(this);
  }

  public resetRotation() {
    super.resetRotation();

    this.moveTooth();
    this.geometry.computeBoundingBox();
    this.geometry.computeBoundsTree();
    this.geometry.boundsTree?.refit();
    this.jaw.onActiveToothTransformEnd(this);
  }

  /**
   * Cuts the tooth mesh below the gum line defined by mapped gum vertices.
   * Keeps the crown part (above the gum line) and removes the root part.
   */
  public cutBelowGumLine() {
    const gumVerticesMap = this.getVerticesAroundGum();
    if (!gumVerticesMap || gumVerticesMap.size === 0) {
      console.warn(`cutBelowGumLine: No gum vertices mapped to tooth ${this.FDI}.`);
      return;
    }
    
    // 1. Identify boundary vertices on the tooth mesh
    const boundaryVertices = new Set(gumVerticesMap.keys());
    
    // 2. Identify a seed vertex guaranteed to be on the crown (e.g., highest point)
    this.geometry.computeBoundingBox();
    
    let seed: Vertex | null = null;
    let maxVal = -Infinity;
    
    // Use +Y as default "up" for teeth in many systems, or try to infer from bounding box
    for (const v of this.halfEdges.vertices) {
      if (v.position.y > maxVal) {
        maxVal = v.position.y;
        seed = v;
      }
    }
    
    if (!seed) {
        console.warn(`cutBelowGumLine: Could not find seed vertex for tooth ${this.FDI}.`);
        return;
    }
    
    if (boundaryVertices.has(seed)) {
      // Edge case: seed is on boundary. Try to find a neighbor not on boundary.
       for (const n of seed.adjacentVertices()) {
         if (!boundaryVertices.has(n)) {
           seed = n;
           break;
         }
       }
    }

    // 3. Flood fill (BFS) to find all vertices connected to seed without crossing boundary
    const keptVertices = new Set<Vertex>();
    const queue: Vertex[] = [seed];
    keptVertices.add(seed);
    
    while (queue.length > 0) {
      const v = queue.shift()!;
      
      for (const n of v.adjacentVertices()) {
        if (!keptVertices.has(n) && !boundaryVertices.has(n)) {
          keptVertices.add(n);
          queue.push(n);
        } else if (boundaryVertices.has(n)) {
          // We reached the boundary. We keep the boundary vertex itself to close the shape?
          if (!keptVertices.has(n)) {
            keptVertices.add(n);
            // Do NOT push to queue - stop propagation here
          }
        }
      }
    }
    
    // 4. Rebuild mesh keeping only faces where ALL 3 vertices are in keptVertices
    const keptFaces = new Set<Face>();
    
    this.halfEdges.faces.forEach(f => {
        const v1 = f.halfedge.vertex;
        const v2 = f.halfedge.next.vertex;
        const v3 = f.halfedge.prev.vertex;
        
        if (keptVertices.has(v1) && keptVertices.has(v2) && keptVertices.has(v3)) {
            keptFaces.add(f);
        }
    });
    
    if (keptFaces.size === 0) {
        console.warn('cutBelowGumLine: Cutting removed all faces! Aborting.');
        return;
    }
    
    // Create new geometry using the excludedBufferGeometry logic (which builds a new geom from subset)
    // excludedBufferGeometry takes a set of FACES TO EXCLUDE.
    const facesToExclude = new Set<Face>();
    this.halfEdges.faces.forEach(f => {
        if (!keptFaces.has(f)) {
            facesToExclude.add(f);
        }
    });
    
    if (facesToExclude.size === 0) {
        console.log('cutBelowGumLine: No faces to exclude (tooth is fully above gum).');
        return;
    }
    
    const result = this.halfEdges.excludedBufferGeometry(facesToExclude);
    
    // Update the mesh geometry
    this.geometry.dispose();
    this.geometry = result.newGeometry;
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundingBox();
    this.geometry.computeBoundsTree();
    
    // Re-initialize halfedges structure from new geometry
    this.halfEdges = new HalfedgeDS(this.geometry);
    
    console.log(`cutBelowGumLine: Tooth ${this.FDI} cut. Removed ${facesToExclude.size} faces.`);
  }
}
